import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, Paper, Snackbar, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Typography, useTheme,
} from '@mui/material';
import {
  Layers as LayersIcon, CheckCircleOutline as CleanIcon, Refresh as RefreshIcon,
  Build as RebuildIcon,
} from '@mui/icons-material';
import { useVenue } from '../../../../context/VenueContext';
import PageHeader from '../../../../components/venue/PageHeader';
import EmptyState from '../../../../components/venue/EmptyState';
import costLayerDriftService from '../../../../services/costLayerDriftService';
import type { LayerDriftReport, LayerDriftGroup } from '../../../../services/costLayerDriftService';
import { formatQty } from '../../../../utils/quantityFormat';
import { fromMinorUnitsOrNull } from '../../../../utils/money';

/**
 * COSTING.1a — Cost Layer Drift (ADMIN_ONLY). Read-only comparison of Σ open
 * cost-layer remainingQty vs Σ balance quantity per (item, location, ownership),
 * for activated venues. A drifted group can be REBUILT by re-genesis (the
 * confirm dialog states exactly what that discards). Quantities + costs; money
 * always via fromMinorUnits.
 */

const KIND_LABEL: Record<LayerDriftGroup['kind'], string> = {
  MISMATCH: 'Mismatch',
  ORPHAN_LAYERS: 'Orphan layers',
  MISSING_LAYERS: 'Missing layers',
};
const KIND_COLOR: Record<LayerDriftGroup['kind'], 'error' | 'warning'> = {
  MISMATCH: 'error',
  ORPHAN_LAYERS: 'warning',
  MISSING_LAYERS: 'warning',
};

const fmtMoney = (minor: number | null | undefined): string => {
  const major = fromMinorUnitsOrNull(minor);
  return major == null ? '—' : major.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const CostLayerDriftPage: React.FC = () => {
  const theme = useTheme();
  const { venueId } = useVenue();

  const [report, setReport] = useState<LayerDriftReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [confirmTarget, setConfirmTarget] = useState<LayerDriftGroup | null>(null);
  const [rebuilding, setRebuilding] = useState(false);
  const [snack, setSnack] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!venueId) return;
    setLoading(true);
    setError(null);
    try {
      setReport(await costLayerDriftService.getLayerDrift(venueId));
    } catch (err: any) {
      setError(err?.response?.data?.error?.message ?? 'Failed to load cost-layer drift report.');
    } finally {
      setLoading(false);
    }
  }, [venueId]);

  useEffect(() => { load(); }, [load]);

  const runRebuild = async () => {
    if (!venueId || !confirmTarget) return;
    setRebuilding(true);
    try {
      const res = await costLayerDriftService.rebuildGroup(venueId, {
        inventoryItemId: confirmTarget.inventoryItemId,
        locationId: confirmTarget.locationId,
        ownershipAccountId: confirmTarget.ownershipAccountId,
      });
      setSnack(
        `Rebuilt ${confirmTarget.itemName ?? 'item'} @ ${confirmTarget.locationName ?? 'location'}: ` +
        `replaced ${res.deleted} layer(s) with ${res.created} at ${fmtMoney(res.unitCost)} (${formatQty(res.quantity)} on hand).`,
      );
      setConfirmTarget(null);
      await load();
    } catch (err: any) {
      setSnack(err?.response?.data?.error?.message ?? 'Rebuild failed.');
    } finally {
      setRebuilding(false);
    }
  };

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <PageHeader
        icon={<LayersIcon />}
        title="Cost Layer Drift"
        subtitle="Compares open FIFO cost layers against stored balances per item, location and ownership. Drifted groups can be rebuilt from the current balance."
        actions={
          <Button size="small" startIcon={<RefreshIcon />} onClick={load} disabled={loading} sx={{ textTransform: 'none' }}>
            Refresh
          </Button>
        }
      />

      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}
      {error && !loading && <Alert severity="error">{error}</Alert>}

      {/* Not yet activated — costing is dormant, nothing to compare. */}
      {!loading && !error && report && !report.active && (
        <EmptyState
          icon={LayersIcon}
          message="Cost tracking is not active for this venue yet."
          hint="Cost layers appear once costing is activated. Until then, there is nothing to reconcile."
        />
      )}

      {!loading && !error && report && report.active && report.clean && (
        <Paper sx={{ p: 5, borderRadius: 2, textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <CleanIcon sx={{ fontSize: 48, color: '#1a7f37', mb: 1 }} />
          <Typography variant="h6" fontWeight={700}>No cost-layer drift detected</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Layers and balances agree across all {report.summary.totalGroupsChecked.toLocaleString()} groups.
          </Typography>
        </Paper>
      )}

      {!loading && !error && report && report.active && !report.clean && (
        <>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            <Chip color="error" label={`${report.summary.mismatched} mismatched`} />
            <Chip color="warning" label={`${report.summary.orphanLayers} orphan layers`} />
            <Chip color="warning" label={`${report.summary.missingLayers} missing layers`} />
            <Chip variant="outlined" label={`${report.summary.totalGroupsChecked} groups checked`} />
          </Stack>

          <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Item</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Layers</TableCell>
                    <TableCell align="right">Balance</TableCell>
                    <TableCell align="right">Difference</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.groups.map((g, i) => (
                    <React.Fragment key={`${g.inventoryItemId}-${g.locationId}-${g.ownershipAccountId}-${i}`}>
                      <TableRow hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600} noWrap title={g.itemName ?? g.inventoryItemId}>
                            {g.itemName ?? `(item ${g.inventoryItemId.slice(-6)})`}
                          </Typography>
                        </TableCell>
                        <TableCell><Typography variant="body2" color="text.secondary">{g.locationName ?? '—'}</Typography></TableCell>
                        <TableCell>
                          <Chip size="small" color={KIND_COLOR[g.kind]} label={KIND_LABEL[g.kind]} sx={{ fontWeight: 600 }} />
                          {/* COSTING.1d — flag negative-stock (provisional) groups. */}
                          {g.hasProvisionals && (
                            <Chip size="small" variant="outlined" label="negative stock (provisional)" sx={{ ml: 0.5, fontSize: '0.65rem' }} />
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{g.layerRemaining == null ? '—' : formatQty(g.layerRemaining)}</TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{g.balanceQuantity == null ? '—' : formatQty(g.balanceQuantity)}</TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 700, color: g.difference === 0 ? theme.palette.text.disabled : theme.palette.error.main }}>
                          {g.difference > 0 ? `+${formatQty(g.difference)}` : formatQty(g.difference)}
                        </TableCell>
                        <TableCell align="right">
                          <Button size="small" startIcon={<RebuildIcon />} onClick={() => setConfirmTarget(g)} sx={{ textTransform: 'none' }}>
                            Rebuild
                          </Button>
                        </TableCell>
                      </TableRow>
                      {/* Per-layer detail for mismatches (diagnosis). */}
                      {g.kind === 'MISMATCH' && g.layers.length > 0 && (
                        <TableRow>
                          <TableCell colSpan={7} sx={{ py: 0.5, bgcolor: theme.palette.action.hover }}>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, pl: 2 }}>
                              {g.layers.map(l => (
                                <Chip
                                  key={l.layerId}
                                  size="small"
                                  variant="outlined"
                                  label={`${l.sourceType} · ${formatQty(l.remainingQty)}/${formatQty(l.originalQty)} @ ${fmtMoney(l.unitCost)}${l.costBasis !== 'ACTUAL' ? ` (${l.costBasis})` : ''}`}
                                  sx={{ fontFamily: 'monospace', fontSize: '0.68rem' }}
                                />
                              ))}
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}

      {/* Rebuild confirm — states EXACTLY what re-genesis discards. */}
      <Dialog open={!!confirmTarget} onClose={() => !rebuilding && setConfirmTarget(null)}>
        <DialogTitle>Rebuild cost layers?</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            This replaces the {confirmTarget?.layerRemaining != null ? 'open ' : ''}cost layers for{' '}
            <strong>{confirmTarget?.itemName ?? 'this item'}</strong> at{' '}
            <strong>{confirmTarget?.locationName ?? 'this location'}</strong> with a single re-baselined
            layer at the current balance cost.
            <Box component="span" sx={{ display: 'block', mt: 1.5, color: 'warning.main' }}>
              Per-layer history for this group is discarded and the stock is re-valued at its current
              last cost (GENESIS basis). This cannot be undone. Ledger-replay rebuild arrives in a later
              costing phase.
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmTarget(null)} disabled={rebuilding}>Cancel</Button>
          <Button onClick={runRebuild} variant="contained" color="warning" disabled={rebuilding} startIcon={rebuilding ? <CircularProgress size={16} color="inherit" /> : <RebuildIcon />}>
            Rebuild group
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snack} autoHideDuration={5000} onClose={() => setSnack(null)} message={snack ?? ''} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
};

export default CostLayerDriftPage;
